import { lazy, Suspense } from "react";
import {
  Wallet, Receipt, FileInput, ClipboardList, Scale, ShoppingCart, BarChart3, Loader2,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/cn";
import { ReceivablesTab, PayablesTab, PurchasingTab } from "@/pages/finance/FinancePage";

const AnalyticsPage = lazy(() => import("@/pages/analytics/AnalyticsPage"));

export type DashboardTab =
  | "finance" | "receivables" | "payables" | "purchasing"
  | "reconciliation" | "sales" | "analytics";

export const DASHBOARD_TABS: Array<{
  slug: DashboardTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { slug: "finance", label: "Finance", icon: Wallet },
  { slug: "receivables", label: "Receivables", icon: Receipt },
  { slug: "payables", label: "Payables", icon: FileInput },
  { slug: "purchasing", label: "Purchasing", icon: ClipboardList },
  { slug: "reconciliation", label: "Reconciliation", icon: Scale },
  { slug: "sales", label: "Sales", icon: ShoppingCart },
  { slug: "analytics", label: "Analytics", icon: BarChart3 },
];

interface DashboardOverlayProps {
  open: boolean;
  activeTab: DashboardTab | null;
  onTabChange: (slug: DashboardTab) => void;
  onClose: () => void;
}

export function DashboardOverlay({ open, activeTab, onTabChange, onClose }: DashboardOverlayProps) {
  const tab = activeTab ?? "finance";
  const Header = DASHBOARD_TABS.find((t) => t.slug === tab);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] p-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b px-5 py-3 pr-12">
          <div className="flex items-center gap-2 text-sm font-semibold">
            {Header && <Header.icon className="h-4 w-4 text-blue-500" />}
            {Header?.label}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Suspense fallback={
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          }>
            {tab === "finance" && <FinanceOverview />}
            {tab === "receivables" && <ReceivablesTab />}
            {tab === "payables" && <PayablesTab />}
            {tab === "purchasing" && <PurchasingTab />}
            {tab === "reconciliation" && <Placeholder label="Reconciliation" />}
            {tab === "sales" && <Placeholder label="Sales" />}
            {tab === "analytics" && <AnalyticsPage />}
          </Suspense>
        </div>

        {/* Inner tab strip */}
        <div className="border-t bg-background/95 backdrop-blur-sm py-2 px-3">
          <div className="flex items-center justify-center gap-1 flex-wrap">
            {DASHBOARD_TABS.map((t) => {
              const isActive = t.slug === tab;
              return (
                <button
                  key={t.slug}
                  type="button"
                  onClick={() => onTabChange(t.slug)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs",
                    isActive
                      ? "text-blue-600 dark:text-blue-400 bg-blue-500/10"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                >
                  <t.icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Finance overview shows all three financial sub-tabs at-a-glance
function FinanceOverview() {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Receivables</h3>
        <ReceivablesTab />
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Payables</h3>
        <PayablesTab />
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Purchasing</h3>
        <PurchasingTab />
      </div>
    </div>
  );
}

function Placeholder({ label }: { label: string }) {
  return (
    <div className="text-center text-sm text-muted-foreground py-16">
      {label} dashboard is coming soon.
    </div>
  );
}
